import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { RotateCcw, Skull } from "lucide-react";
import { usePowerDelete } from "@/context/PowerDeleteContext";

const COLLECTION_LABELS = {
  users: "User", bookings: "Booking", counselor_applications: "Counselor application",
  employee_applications: "Employee application", escalations: "Escalation",
  support_tickets: "Support ticket", employee_shares: "Shared file",
  coupons: "Coupon", services: "Service",
};

function formatCountdown(seconds) {
  if (seconds <= 0) return "expiring…";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m}m left`;
}

export default function PowerDeletedBin() {
  const { refreshToken, bumpRefresh } = usePowerDelete();
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const fetchedAtRef = useRef(Date.now());
  const [, forceTick] = useState(0);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/power-deleted");
      fetchedAtRef.current = Date.now();
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => {
    load();
    const refreshInterval = setInterval(load, 20000);
    const tickInterval = setInterval(() => forceTick(t => t + 1), 30000);
    return () => { clearInterval(refreshInterval); clearInterval(tickInterval); };
  }, []);
  useEffect(() => { load(); /* eslint-disable-line */ }, [refreshToken]);

  const restore = async (item) => {
    setBusyId(item.id);
    try {
      await api.post(`/admin/power-deleted/${item.id}/restore`);
      toast.success(`Restored ${COLLECTION_LABELS[item.source_collection] || item.source_collection}`);
      bumpRefresh();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  if (items.length === 0) return null;

  return (
    <div data-testid="power-deleted-bin" className="mt-12">
      <h2 className="font-serif-display text-2xl mb-4 inline-flex items-center gap-2">
        <Skull className="w-5 h-5" style={{ color: "#b91c1c" }} /> Recently deleted (Power mode)
      </h2>
      <div className="label-eyebrow mb-3">Restorable for 24 hours, then permanently purged</div>
      <div className="space-y-2" data-testid="power-deleted-list">
        {items.map(it => {
          const elapsed = Math.floor((Date.now() - fetchedAtRef.current) / 1000);
          const remaining = Math.max(0, it.seconds_remaining - elapsed);
          return (
            <div key={it.id} data-testid={`power-deleted-row-${it.id}`}
              className="card-soft p-3 flex flex-wrap items-center justify-between gap-3"
              style={{ borderColor: "rgba(185,28,28,0.25)" }}>
              <div>
                <div className="font-medium">
                  {it.label} <span className="text-xs" style={{ color: "var(--text-secondary)" }}>· {COLLECTION_LABELS[it.source_collection] || it.source_collection}</span>
                </div>
                <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
                  deleted {new Date(it.deleted_at).toLocaleString()} · {formatCountdown(remaining)}
                </div>
              </div>
              <button data-testid={`power-deleted-restore-${it.id}`} disabled={busyId === it.id}
                onClick={() => restore(it)}
                className="btn-outline text-xs px-3 py-1.5 inline-flex items-center gap-1">
                <RotateCcw className="w-3.5 h-3.5" /> Restore
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
